import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Radio, Loader2, Crosshair, Mic, Truck } from "lucide-react";
import { toast } from "sonner";

/**
 * MarketplaceLiveNew — Go-Live composer.
 *
 * Handles three entry contexts, all off the same form:
 *   • Marketplace  → /marketplace/live/new
 *   • Artist page  → /marketplace/live/new?artist_id={aid}   (band goes live)
 *   • Mobile biz   → /marketplace/live/new?checkin=1         (food truck / pop-up
 *                    goes live from current GPS; captured on submit)
 *
 * On success we navigate straight to the watch page so the host can copy
 * their RTMP key and press "Go live".
 */
export default function MarketplaceLiveNew() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const artistId = params.get("artist_id") || "";
  const isCheckin = params.get("checkin") === "1";
  const isShop = params.get("shop") === "1";

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [scheduledFor, setScheduledFor] = useState("");
  const [checkinLabel, setCheckinLabel] = useState("");
  const [coords, setCoords] = useState({ lat: null, lng: null });
  const [detecting, setDetecting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [artistName, setArtistName] = useState("");
  // Shop mode — fetches the caller's active products and lets them pick which
  // to attach as buyable items on the watch page (TikTok-Shop-style).
  const [shopProducts, setShopProducts] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);

  // If the caller passed ?artist_id=..., verify we own the profile — otherwise
  // the backend will ignore the field silently, but the UI should reflect it.
  useEffect(() => {
    if (!artistId) return;
    api.get(`/artists/${artistId}`)
      .then(({ data }) => setArtistName(data?.artist?.stage_name || ""))
      .catch(() => setArtistName(""));
  }, [artistId]);

  // Autofill the label from the business's most recent ping when in checkin mode
  useEffect(() => {
    if (!isCheckin) return;
    const loc = user?.current_location;
    if (loc?.label) setCheckinLabel(loc.label);
    if (loc?.lat != null && loc?.lng != null) setCoords({ lat: loc.lat, lng: loc.lng });
  }, [isCheckin, user]);

  // Shop mode — fetch the caller's active products so they can pick which
  // ones to attach for TikTok-Shop-style live selling. Preselects all active
  // products so the common case is one click.
  useEffect(() => {
    if (!isShop) return;
    api.get("/shop/products/mine")
      .then(({ data }) => {
        const items = (data?.items || []).filter((p) => p.is_active && p.in_stock);
        setShopProducts(items);
        setSelectedProductIds(items.map((p) => p.id).slice(0, 25));
      })
      .catch(() => setShopProducts([]));
  }, [isShop]);

  const toggleProduct = (pid) => {
    setSelectedProductIds((prev) => {
      if (prev.includes(pid)) return prev.filter((x) => x !== pid);
      if (prev.length >= 25) {
        toast.error("You can attach up to 25 products per stream");
        return prev;
      }
      return [...prev, pid];
    });
  };

  const detect = () => {
    if (!("geolocation" in navigator)) {
      toast.error("Your browser can't share GPS coordinates");
      return;
    }
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setCoords({ lat: p.coords.latitude, lng: p.coords.longitude });
        toast.success(`GPS locked (${p.coords.latitude.toFixed(4)}, ${p.coords.longitude.toFixed(4)})`);
        setDetecting(false);
      },
      (err) => {
        toast.error(`Location denied: ${err.message}`);
        setDetecting(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (title.trim().length < 3) {
      toast.error("Give your stream a title (3+ characters)");
      return;
    }
    if (isCheckin && checkinLabel.trim().length < 2) {
      toast.error("Add a short check-in label so viewers know where you're broadcasting from");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
      };
      if (scheduledFor) payload.scheduled_for = new Date(scheduledFor).toISOString();
      if (artistId) payload.artist_id = artistId;
      if (isCheckin) {
        payload.checkin_label = checkinLabel.trim();
        if (coords.lat != null && coords.lng != null) {
          payload.checkin_lat = coords.lat;
          payload.checkin_lng = coords.lng;
        }
      }
      if (isShop && selectedProductIds.length > 0) {
        payload.product_ids = selectedProductIds;
      }
      const { data } = await api.post("/marketplace/live/sessions", payload);
      toast.success("Session created — copy your RTMP key on the next page");
      nav(`/marketplace/live/${data.session.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center" data-testid="live-new-signin">
        <Radio size={30} className="mx-auto text-[#B31E1E] mb-3" />
        <h1 className="font-display text-2xl">Sign in to go live</h1>
        <Link to="/login" className="mt-4 inline-block"><Button className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white">Sign in</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-10 py-10" data-testid="live-new-page">
      <Link to="/marketplace/live" className="text-xs font-mono text-gray-500 hover:text-[#B31E1E]">← Back to Live</Link>
      <header className="mt-3">
        <div className="overline text-[#B31E1E]">Marketplace · Live · New</div>
        <h1 className="font-display text-4xl tracking-tighter mt-1 flex items-center gap-3">
          {artistId ? <Mic size={26} className="text-[#B31E1E]" /> : isCheckin ? <Truck size={26} className="text-emerald-700" /> : <Radio size={26} className="text-[#B31E1E]" />}
          {artistName ? `Go live as ${artistName}` : isCheckin ? "Go live from your check-in" : "Start a live stream"}
        </h1>
        <p className="mt-2 text-gray-600 max-w-xl">
          Fill in a title + optional description. On the next page we'll show you your RTMP server + stream key.
          Point OBS or Streamlabs at those, then press <em>Go live</em>.
        </p>
      </header>

      <Card className="rounded-md p-6 mt-6">
        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label htmlFor="live-title" className="text-xs font-mono uppercase tracking-wider text-gray-600">
              Title
            </Label>
            <Input
              id="live-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={140}
              placeholder="Grand-opening flash sale — 30% off everything"
              className="rounded-sm mt-1"
              data-testid="live-new-title"
            />
          </div>
          <div>
            <Label htmlFor="live-desc" className="text-xs font-mono uppercase tracking-wider text-gray-600">
              Description <span className="text-gray-400 font-normal normal-case">(optional)</span>
            </Label>
            <Textarea
              id="live-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={1000}
              rows={3}
              placeholder="What are you showing off?"
              className="rounded-sm mt-1"
              data-testid="live-new-desc"
            />
          </div>
          <div>
            <Label htmlFor="live-schedule" className="text-xs font-mono uppercase tracking-wider text-gray-600">
              Schedule for later <span className="text-gray-400 font-normal normal-case">(optional)</span>
            </Label>
            <Input
              id="live-schedule"
              type="datetime-local"
              value={scheduledFor}
              onChange={(e) => setScheduledFor(e.target.value)}
              className="rounded-sm mt-1 font-mono"
              data-testid="live-new-schedule"
            />
            <p className="text-[11px] text-gray-500 mt-1">Leave blank to start immediately.</p>
          </div>

          {isCheckin && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 space-y-3" data-testid="live-new-checkin-block">
              <div className="flex items-center gap-2 text-emerald-700">
                <Truck size={14} />
                <span className="text-xs font-mono uppercase tracking-wider font-semibold">Broadcasting from your check-in</span>
              </div>
              <div>
                <Label htmlFor="live-checkin-label" className="text-xs font-mono uppercase tracking-wider text-gray-600">
                  Where are you?
                </Label>
                <Input
                  id="live-checkin-label"
                  value={checkinLabel}
                  onChange={(e) => setCheckinLabel(e.target.value)}
                  maxLength={140}
                  placeholder="2nd & Elm — parked in front of Berkeley Bowl"
                  className="rounded-sm mt-1"
                  data-testid="live-new-checkin-label"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-gray-600">
                  GPS pin <span className="text-gray-400 font-normal normal-case">(optional)</span>
                </Label>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <Button type="button" variant="outline" onClick={detect} disabled={detecting} className="rounded-[35px]" data-testid="live-new-gps">
                    {detecting ? (<><Loader2 size={13} className="mr-1.5 animate-spin" /> Detecting…</>) : (<><Crosshair size={13} className="mr-1.5" /> Use my GPS</>)}
                  </Button>
                  {coords.lat != null && (
                    <span className="text-[11px] font-mono text-emerald-700 bg-white px-2 py-1 rounded-full border border-emerald-200">
                      {coords.lat.toFixed(4)}, {coords.lng.toFixed(4)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {isShop && (
            <div className="rounded-md border border-[#B31E1E]/30 bg-red-50/30 p-4 space-y-3" data-testid="live-new-shop-block">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-[#B31E1E]">
                  <Radio size={14} />
                  <span className="text-xs font-mono uppercase tracking-wider font-semibold">Shop — pick items to sell live</span>
                </div>
                <span className="text-[11px] text-gray-500 font-mono">{selectedProductIds.length}/25 selected</span>
              </div>
              {shopProducts.length === 0 ? (
                <p className="text-sm text-gray-600">You have no active shop products. <Link to="/shop/manage?new=1" className="text-[#B31E1E] hover:underline">Add one →</Link></p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto" data-testid="live-new-product-checklist">
                  {shopProducts.map((p) => {
                    const on = selectedProductIds.includes(p.id);
                    return (
                      <button
                        type="button"
                        key={p.id}
                        onClick={() => toggleProduct(p.id)}
                        className={`text-left rounded-md border p-2 transition-colors ${on ? "border-[#B31E1E] bg-white shadow-sm" : "border-border bg-white hover:border-[#B31E1E]/50"}`}
                        data-testid={`live-new-product-${p.id}`}
                      >
                        <div className="flex items-start gap-2">
                          <div className={`h-4 w-4 rounded-sm border-2 shrink-0 mt-0.5 ${on ? "bg-[#B31E1E] border-[#B31E1E]" : "border-gray-300"}`}>
                            {on && <span className="block text-white text-[10px] text-center leading-none pt-[1px]">✓</span>}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold truncate">{p.title}</div>
                            <div className="text-[10px] text-gray-500 font-mono">${(p.price_cents / 100).toFixed(2)}</div>
                          </div>
                          {p.photos?.[0] && (
                            <img src={p.photos[0]} alt="" className="h-10 w-10 rounded object-cover shrink-0" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <Button type="submit" disabled={busy} className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white" data-testid="live-new-submit">
              {busy ? (<><Loader2 size={14} className="mr-1.5 animate-spin" /> Creating…</>) : (<><Radio size={14} className="mr-1.5" /> Create session</>)}
            </Button>
            <Link to="/marketplace/live" className="text-xs text-gray-500 hover:text-[#B31E1E]">Cancel</Link>
          </div>
        </form>
      </Card>
    </div>
  );
}
